import axios from "axios";
import { DEMO_PORTAL_API_URL } from "../Model";
import { logger } from "../LogUtils";

export const ACTION_TYPES = {
  GET_STATIC_CONTENT: "getStaticContent",
  GET_STATIC_CONTENT_OPTIONS: "getStaticContentOptions",
  CREATE_STATIC_CONTENT: "createStaticContent",
  UPDATE_STATIC_CONTENT: "updateStaticContent",

  // Demo, variants and variant versions
  CREATE_DEMO: "createDemo",
  DELETE_DEMO: "deleteDemo",
  CREATE_VARIANT: "createVariant",
  CREATE_VARIANT_VERSION: "createVariantVersion",
  DELETE_VARIANT: "deleteVariant",
  DELETE_MEDIA: "deleteMedia",
  DELETE_SUPPORTING_MEDIA: "deleteSupportingMedia",
  EXPORT_DEMO_VARIANT: "exportDemoVariant",
  LIST_DEMOS: "listDemos",
  LIST_VARIANT_VERSIONS: "listVariantVersions",
  GET_DEMO: "getDemo",
  GET_MEDIA_UPLOAD_URL: "getMediaUploadUrl",
  GET_SUPPORTING_MEDIA_UPLOAD_URL: "getSupportingMediaUploadUrl",
  MARK_VARIANT_AS_DEFAULT: "markVariantAsDefault",
  MEDIA_UPLOADED: "mediaUploaded",
  PUBLISH_VARIANT_VERSION: "publishVariantVersion",
  SAVE_DEMO: "saveDemo",
  SAVE_VARIANT: "saveVariant",
  SAVE_VARIANT_VERSION_PAGES: "saveVariantVersionPages",
  SUPPORTING_MEDIA_UPLOADED: "supportingMediaUploaded",
  RENAME_MEDIA: "renameMedia",

  LIST_USER_FEEDBACK: "listUserFeedback",
  INSERT_USER_FEEDBACK: "insertUserFeedback",
  OPEN_USER_FEEDBACK: "openUserFeedback",
  CLOSE_USER_FEEDBACK: "closeUserFeedback",
  SAVE_USER_FEEDBACK: "saveUserFeedback",
  SAVE_USER_FEEDBACK_ASSIGNED: "saveUserFeedbackAssigned",
  GET_USER_FEEDBACK: "getUserFeedback",
  DOWNLOAD_USER_FEEDBACK: "downloadUserFeedback",

  LIST_RELEASE_NOTES: "listReleaseNotes",
  CREATE_RELEASE_NOTE: "createReleaseNote",
  UPDATE_RELEASE_NOTE: "updateReleaseNote",
  GET_RELEASE_NOTE: "getReleaseNote",

  LIST_ROADMAP_ITEMS: "listRoadmapItems",
  CREATE_ROADMAP_ITEM: "createRoadmapItem",
  UPDATE_ROADMAP_ITEM: "updateRoadmapItem",
  GET_ROADMAP_ITEM: "getRoadmapItem",

  LIST_USERS: "listUsers",
  CREATE_USER: "createUser",
  ENABLE_USER: "enableUser",
  RESET_USER: "resetUser",
  DISABLE_USER: "disableUser",
  DELETE_USER: "deleteUser",
  GET_USER: "getUser",
  UPDATE_USER_GROUPS: "updateUserGroups",

  // Metrics
  GET_METRICS: "getMetrics",
  GET_DEMO_METRICS_SUMMARY: "getDemoMetricsSummary",
  GET_VARIANT_METRICS_SUMMARY: "getVariantMetricsSummary",
  GET_USER_SUMMARY: "getUserSummary",
  GET_CUMULATIVE_USERS: "getCumulativeUsers",
  RECORD_DEMO_METRIC: "recordDemoMetric",

  // Profile
  GET_PROFILE: "getProfile",
  ONBOARD_PROFILE: "onboardProfile",

  // Registration
  REGISTER_PARTNER: "registerPartner",
  LIST_REGISTRATIONS: "listRegistrations",
  LIST_PARTNER_DOMAINS: "listPartnerDomains",
  CREATE_PARTNER_DOMAIN: "createPartnerDomain",
  DELETE_PARTNER_DOMAIN: "deletePartnerDomain",
  APPROVE_REGISTRATION: "approveRegistration",
  DENY_REGISTRATION: "denyRegistration",

  // Chat
  CREATE_USER_CHAT: "createUserChat",
  LIST_USER_CHATS: "listUserChats",
  GET_USER_CHAT: "getUserChat",
  CONTINUE_CHAT: "continueChat",

  // Demo Play History
  CREATE_DEMO_PLAY: "createDemoPlay",
  UPDATE_DEMO_PLAY_ATTRIBUTION: "updateDemoPlayAttribution",
  LIST_USER_DEMO_PLAYS: "listUserDemoPlays",
  GET_ALL_DEMO_PLAYS_ATTRIBUTION_SUMMARY: "getAllDemoPlaysAttributionSummary",
  COPY_YOUTUBE_LINK: "copyYoutubeLink",
  GET_SUPPORTING_MEDIA_DOWNLOAD_URL: "getSupportingMediaDownloadUrl",
} as const;

type DataWithParams = {
  id: string;
  idFieldName: string;
};
interface ApiDataProps {
  action: (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES];
  labelLogName: string;
}
interface GetDataNoParamsProps extends ApiDataProps {
  dataField: string;
}
interface GetDataWithParamsProps extends GetDataNoParamsProps {
  params: DataWithParams;
  fields?: Record<string, any>;
}
interface CreateDataProps extends ApiDataProps {
  fields: Record<string, any>;
  dataField?: string;
  urlOverride?: string;
}
interface PostDataProps extends CreateDataProps {
  labelActionName: string;
}
interface UpdateDataProps extends CreateDataProps {
  id: string;
  idFieldName: string;
  labelActionNameOverride?: string;
}

/**
 * Gets data from DynamoDB
 */
export async function getDataNoParams({
  action,
  labelLogName,
  dataField,
}: GetDataNoParamsProps) {
  try {
    const body = {
      action: action,
    };

    logger.info(`About get ${labelLogName}: ${JSON.stringify(body, null, 2)}`);

    const response = await axios.post(DEMO_PORTAL_API_URL, body, {
      withCredentials: true,
    });

    if (!response.data.success) {
      logger.error(`Failed to get ${labelLogName}: ${response.data.cause}`);
      throw new Error(response.data.cause);
    }

    logger.info(
      `Got ${labelLogName}: ${JSON.stringify(response.data, null, 2)}`
    );

    return response.data[dataField];
  } catch (error) {
    logger.error(`Failed to get ${labelLogName}`, error);
    throw error;
  }
}

export async function getDataWithParams({
  action,
  labelLogName,
  dataField,
  params,
  fields,
}: GetDataWithParamsProps) {
  try {
    const body = {
      action: action,
      [params.idFieldName]: params.id,
      ...fields,
    };

    logger.info(`About get ${labelLogName}: ${JSON.stringify(body, null, 2)}`);

    const response = await axios.post(DEMO_PORTAL_API_URL, body, {
      withCredentials: true,
    });

    if (!response.data.success) {
      logger.error(`Failed to get ${labelLogName}: ${response.data.cause}`);
      throw new Error(response.data.cause);
    }

    // logger.info(
    //   `Got ${labelLogName}: ${JSON.stringify(response.data, null, 2)}`
    // );

    return response.data[dataField];
  } catch (error) {
    logger.error(`Failed to get ${labelLogName}`, error);
    throw error;
  }
}

/**
 * Helper function to expose put data for uploads
 */
export const putData = async (url: string, body: any, options: any) => {
  try {
    await axios.put(url, body, options);
  } catch (error) {
    logger.error(`Failed to put data`, error);
    throw error;
  }
};

/**
 * Helper function to expose get data for downloads
 */
export const getDataAsBlob = async (url: string) => {
  try {
    const response = await axios.get(url, {
      withCredentials: true,
      responseType: "blob",
    });
    const contentType = response.headers["content-type"];
    const blob = new Blob([response.data], { type: contentType });
    return blob;
  } catch (error) {
    logger.error(`Failed to get data from url: ${url}`, error);
    throw error;
  }
};

/**
 * Strongly typed request response API
 */
export const postTypedData = async <T, R>(
  action: string,
  request: T,
  labelLogName: string
): Promise<R> => {
  try {
    const body = {
      action: action,
      request: request,
    };

    const response = await axios.post(DEMO_PORTAL_API_URL, body, {
      withCredentials: true,
    });

    if (!response.data.success) {
      logger.error(`Failed to ${labelLogName}: ${response.data.cause}`);
      throw new Error(response.data.cause);
    }

    return response.data.response as R;
  } catch (error) {
    logger.error(`Failed to ${labelLogName}`, error);
    throw error;
  }
};

export async function postData({
  action,
  fields,
  labelLogName,
  dataField,
  labelActionName,
  urlOverride = undefined,
}: PostDataProps) {
  try {
    const body = {
      action: action,
      ...fields,
    };

    logger.info(
      `About ${labelActionName} ${labelLogName}: ${JSON.stringify(
        body,
        null,
        2
      )}`
    );

    const response = await axios.post(
      urlOverride ? urlOverride : DEMO_PORTAL_API_URL,
      body,
      {
        withCredentials: true,
      }
    );

    if (!response.data.success) {
      logger.error(
        `Failed to ${labelActionName} ${labelLogName}: ${response.data.cause}`
      );
      throw new Error(response.data.cause);
    }

    if (dataField) {
      return response.data[dataField];
    }
  } catch (error) {
    logger.error(`Failed to ${labelActionName} ${labelLogName}`, error);
    throw error;
  }
}

export async function createData({
  action,
  fields,
  labelLogName,
  dataField,
  urlOverride = undefined,
}: CreateDataProps) {
  return await postData({
    action,
    fields,
    labelLogName,
    dataField,
    labelActionName: "create",
    urlOverride,
  });
}

export async function updateData({
  action,
  fields,
  labelLogName,
  id,
  idFieldName,
  labelActionNameOverride,
}: UpdateDataProps) {
  fields[idFieldName] = id;
  return await postData({
    action,
    fields,
    labelLogName,
    labelActionName: labelActionNameOverride
      ? labelActionNameOverride
      : "update",
  });
}
